import {
  createRootRoute, createRoute, createRouter,
  RouterProvider, Outlet, redirect,
} from '@tanstack/react-router';
import { TopBar } from './components/TopBar.tsx';
import { Header } from './components/Header.tsx';
import { BottomNav } from './components/BottomNav.tsx';
import { Sidebar } from './components/Sidebar.tsx';
import { Login } from './pages/Login.tsx';
import { SsoHandoff } from './pages/SsoHandoff.tsx';
import { Profile } from './pages/Profile.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { OrgUsers } from './pages/OrgUsers.tsx';
import { Settings } from './pages/Settings.tsx';
import { OrgSettings } from './pages/OrgSettings.tsx';
import { AddressBookSettings } from './features/address-book/AddressBookSettings.tsx';
import { LettersList } from './features/letters/LettersList.tsx';
import { LetterEditor } from './features/letters/LetterEditor.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { useAuthStore } from './store/auth.ts';

function RootLayout(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  return (
    <div className={`min-h-screen flex flex-col ${user ? 'bg-[hsl(var(--chrome))]' : ''}`}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:px-3 focus:py-2 focus:text-sm focus:font-medium"
      >
        މައި ކޮންޓެންޓަށް ދޭ
      </a>
      <TopBar />
      <Sidebar />
      {user && <>
        <div aria-hidden className="hidden md:block fixed top-16 right-64 z-30 size-8 pointer-events-none"
          style={{ background: 'radial-gradient(circle at bottom left, transparent 31px, hsl(var(--chrome)) 32px)' }} />
        <div aria-hidden className="hidden md:block fixed top-16 left-0 z-30 size-8 pointer-events-none"
          style={{ background: 'radial-gradient(circle at bottom right, transparent 31px, hsl(var(--chrome)) 32px)' }} />
      </>}
      <Header />
      <main
        id="main-content"
        tabIndex={-1}
        className={`flex-1 ${user ? 'has-bottom-nav md:ms-64 md:pt-16' : ''}`}
      >
        {user ? (
          <div className="min-h-dvh bg-[hsl(var(--background))] md:min-h-[calc(100dvh-4rem)]">
            <Outlet />
          </div>
        ) : <Outlet />}
      </main>
      <BottomNav />
    </div>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

const requireAuth = (): void => {
  if (!useAuthStore.getState().accessToken) throw redirect({ to: '/login' });
};

const requireOrgAdmin = (): void => {
  const state = useAuthStore.getState();
  if (!state.accessToken) throw redirect({ to: '/login' });
  if (!state.user?.isOrgAdmin) throw redirect({ to: '/' });
};

// Public routes
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: Login });
const ssoHandoffRoute = createRoute({ getParentRoute: () => rootRoute, path: '/sso/handoff', component: SsoHandoff });

// Authed routes
const indexRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/',
  beforeLoad: requireAuth,
  component: Dashboard,
});
const profileRoute = createRoute({ getParentRoute: () => rootRoute, path: '/profile', beforeLoad: requireAuth, component: Profile });

// Letters — available to all org members (no isOrgAdmin gate). /letters/new
// must be declared before /letters/$id so the literal path wins the match.
const lettersListRoute = createRoute({ getParentRoute: () => rootRoute, path: '/letters', beforeLoad: requireAuth, component: LettersList });
const letterNewRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/letters/new',
  beforeLoad: requireAuth, component: () => <LetterEditor />,
});
const letterEditRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/letters/$id',
  beforeLoad: requireAuth,
  component: () => {
    const { id } = letterEditRoute.useParams();
    return <LetterEditor id={id} />;
  },
});

// /dashboard is a common bookmark/typo — the home page lives at '/'. Redirect.
const dashboardRedirectRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/dashboard',
  beforeLoad: () => { throw redirect({ to: '/' }); },
});
// Old /users bookmarks redirect into the settings area.
const usersRedirectRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/users',
  beforeLoad: () => { requireOrgAdmin(); throw redirect({ to: '/settings/users' }); },
});

// Authed + isOrgAdmin routes — Settings shell with nested tabs (Users, Org).
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/settings',
  beforeLoad: requireOrgAdmin, component: Settings,
});
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute, path: '/',
  beforeLoad: () => { throw redirect({ to: '/settings/users' }); },
});
const settingsUsersRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'users', component: OrgUsers });
const settingsOrgRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'org', component: OrgSettings });
const settingsAddressBookRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'address-book', component: AddressBookSettings });

const routeTree = rootRoute.addChildren([
  loginRoute,
  ssoHandoffRoute,
  indexRoute, profileRoute,
  lettersListRoute, letterNewRoute, letterEditRoute,
  dashboardRedirectRoute, usersRedirectRoute,
  settingsRoute.addChildren([
    settingsIndexRoute, settingsUsersRoute, settingsOrgRoute,
    settingsAddressBookRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}

export function AppRouter(): JSX.Element { return <RouterProvider router={router} />; }
